/* eslint-disable no-async-promise-executor */
import { ActionContext } from 'vuex';

import api from '@/api';
import router from '@/router';

import { ApiAccount, ApiAccountUserSetting } from '@/api/types/auth';

interface AuthState {
    account: ApiAccount;
    isAdmin: boolean;
    accountRequestPending: boolean;
}

const auth = {
    namespaced: true,
    state: {
        account: {},
        isAdmin: false,
        accountRequestPending: false,
    },
    getters: {
        userSettingBoolean(state: AuthState) {
            return (settingKey: string): boolean => {
                let value = false;
                if (state.account.UserSettings) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const userSettingsFromServer: any = state.account.UserSettings;
                    const valueFromServer = userSettingsFromServer[`${settingKey}`];

                    if (valueFromServer) {
                        value = valueFromServer === 'True';
                    }
                }

                return value;
            };
        },
        userSettingString(state: AuthState) {
            return (settingKey: string): string | null => {
                let value = null;
                if (state.account.UserSettings) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const userSettingsFromServer: any = state.account.UserSettings;
                    const valueFromServer = userSettingsFromServer[`${settingKey}`];

                    if (valueFromServer) {
                        value = valueFromServer;
                    }
                }

                return value;
            };
        },
        isAdmin(state: AuthState) {
            return state.isAdmin;
        },
        userSettings(state: AuthState) {
            return state.account.UserSettings;
        },
        userId(state: AuthState) {
            return state.account.Id;
        },
    },
    mutations: {
        startAccountRequest(state: AuthState): void {
            state.accountRequestPending = true;
        },
        endAccountRequest(state: AuthState): void {
            state.accountRequestPending = false;
        },
        updateAccount(state: AuthState, account: ApiAccount): void {
            state.account = account;
        },
        updateAdminRole(state: AuthState, isAdmin: boolean): void {
            state.isAdmin = isAdmin;
        },
        updateAccountSettings(state: AuthState, settings: ApiAccountUserSetting): void {
            if (state.account.UserSettings) {
                const userSettings = state.account.UserSettings;
                state.account.UserSettings = { ...userSettings, [`${settings.Key}`]: settings.Value };
            } else {
                state.account = { ...state.account, UserSettings: { [`${settings.Key}`]: settings.Value } };
            }
        },
        clearAccount(state: AuthState): void {
            state.account = {};
            state.isAdmin = false;
        },
    },
    actions: {
        login(
            context: ActionContext<AuthState, null>,
            credentials: { username: string; password: string },
        ): Promise<boolean> {
            return new Promise<boolean>(async (resolve, reject) => {
                try {
                    const token = btoa(`${credentials.username}:${credentials.password}`);
                    const authToken = await api.auth.login(token);

                    window.localStorage.setItem('authcredentials', authToken.data.Token);
                    /* context.dispatch('account'); */
                    resolve(true);
                } catch {
                    reject(new Error('auth_error'));
                }
            });
        },

        logout(context: ActionContext<AuthState, null>): void {
            try {
                window.localStorage.removeItem('authcredentials');
                context.dispatch('groups/clearList', null, { root: true });
                context.dispatch('groups/clearFilters', null, { root: true });
                context.dispatch('device/clearList', null, { root: true });
                context.dispatch('device/clearDates', null, { root: true });
                context.dispatch('device/clearRoutes', null, { root: true });
                context.dispatch('layers/clearList', null, { root: true });
                context.dispatch('ui/clearLanguage', null, { root: true });
                context.dispatch('events/clearList', null, { root: true });
                context.dispatch('clearAccount');
                context.dispatch('device/clearFilters', null, { root: true });
                // TODO - when token will be available: await api.auth.logout(window.localStorage.getItem('authcredentials'));
                // eslint-disable-next-line no-empty
            } catch {
            } finally {
                router.push({ name: 'login' });
            }
        },

        verifyUser(context: ActionContext<AuthState, null>): Promise<boolean> {
            return new Promise<boolean>(async (resolve, reject) => {
                try {
                    if (window.localStorage.getItem('authcredentials') !== null) {
                        try {
                            /* const token = window.localStorage.getItem('authcredentials');
                            const authToken = await api.auth.login(`Bearer ${token}` || '');
                            window.localStorage.setItem('authcredentials', authToken.data.Token); */
                            await context.dispatch('account');
                            resolve(true);
                        } catch {
                            reject(new Error('auth_error'));
                        }
                    } else {
                        reject(new Error('auth_error'));
                    }
                } catch {
                    reject(new Error('storage_error'));
                }
            });
        },

        redeem(context: ActionContext<AuthState, null>, token: string): Promise<boolean> {
            return new Promise<boolean>(async (resolve, reject) => {
                try {
                    const authToken = await api.auth.redeem(`${token}`);

                    window.localStorage.setItem('authcredentials', authToken.data.Token);
                    context.dispatch('account');
                    resolve(true);
                } catch {
                    reject(new Error('auth_error'));
                }
            });
        },

        clearAccount(context: ActionContext<AuthState, null>): void {
            context.commit('clearAccount');
        },

        async account(context: ActionContext<AuthState, null>): Promise<void> {
            context.commit('startAccountRequest');
            try {
                const token = window.localStorage.getItem('authcredentials');
                const response = await api.auth.account(token || '');
                context.commit('updateAccount', {
                    ...response.data,
                });
                // check and set admin flag
                context.commit(
                    'updateAdminRole',
                    response.data.Rolenames
                        ? response.data?.Rolenames.some((e) => e.toLowerCase().includes('admin'))
                        : false,
                );
                context.commit('ui/updateLanguage', response.data?.LanguageCode, { root: true });
            } catch (err) {
                // console.log(err);
            } finally {
                context.commit('endAccountRequest');
            }
        },

        async updateAccount(context: ActionContext<AuthState, null>, userData: ApiAccount): Promise<void> {
            context.commit('startAccountRequest');
            try {
                const token = window.localStorage.getItem('authcredentials');
                await api.auth.updateAccount(token || '', userData);
            } catch (err) {
                // console.log(err);
            } finally {
                context.commit('endAccountRequest');
            }
        },

        updateUserSetting(context: ActionContext<AuthState, null>, userSetting: ApiAccountUserSetting): void {
            context.commit('updateAccountSettings', { Key: userSetting.Key, Value: userSetting.Value });
        },

        async updateAndSaveUserSettings(context: ActionContext<AuthState, null>): Promise<void> {
            try {
                const token = window.localStorage.getItem('authcredentials');
                await api.auth.updateAccount(token || '', { UserSettings: context.state.account.UserSettings });
            } catch (err) {
                // console.log(err);
            }
        },

        async updateFirebaseToken(context: ActionContext<AuthState, null>, tokenFCM: string) {
            try {
                const token = window.localStorage.getItem('authcredentials');
                await api.auth.updateAccount(token || '', {
                    UserSettings: {
                        ...context.state.account.UserSettings,
                        tokenFCM: JSON.stringify([
                            ...JSON.parse(context.state.account.UserSettings.tokenFCM),
                            {
                                token: tokenFCM,
                                timestamp: new Date().getTime(),
                                environment: process.env.VUE_APP_NAME,
                            },
                        ]),
                    },
                });
            } catch (err) {
                // console.log(err);
            }
        },

        async unsubscribeAllDevices(context: ActionContext<AuthState, null>) {
            try {
                const token = window.localStorage.getItem('authcredentials');
                await api.auth.updateAccount(token || '', {
                    UserSettings: {
                        ...context.state.account.UserSettings,
                        allowSubscriptions: 'False',
                        tokenFCM: JSON.stringify([]),
                    },
                });
            } catch (err) {
                // console.log(err);
            }
        },
    },
};

export default auth;
